<template>
  <div class="product-list">
    <!-- 顶部图片 -->
    <!-- <div v-if="topImage" class="top-image-section">
      <img :src="topImage" alt="产品中心" class="top-image">
    </div> -->

    <!-- 系列 Tab -->
    <div class="series-tabs-container">
      <div class="series-tabs">
        <div v-for="series in seriesList" :key="series.id" class="series-tab" :class="{ 'series-tab-active': activeSeriesCode === series.code }" @click="handleSeriesClick(series)">
          <span class="series-name">{{ series.name }}</span>
          <img :src="listIconSrc" alt="" class="series-tab-icon">
        </div>
      </div>
    </div>

    <!-- 产品列表 -->
    <div class="products-container">
      <div v-if="loading" class="loading">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="productList.length === 0" class="empty-products">
        <p>暂无产品</p>
      </div>
      <div v-else class="products-grid">
        <div v-for="product in productList" :key="product.id" class="product-item" @click="handleProductClick(product)">
          <div class="product-image" @click.stop>
            <el-image
              v-if="getProductImage(product)"
              :src="getProductImage(product)"
              :preview-src-list="previewImageList"
              fit="cover"
              class="product-img"
              @error="handleImageError"
            >
              <div slot="error" class="image-placeholder">
                <i class="el-icon-picture"></i>
              </div>
            </el-image>
            <div v-else class="image-placeholder">
              <i class="el-icon-picture"></i>
            </div>
          </div>
          <div class="product-name">{{ getProductName(product) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { queryDynamicHtmlList } from '@/api/menu'
import { getMenuHtmlDetail } from '@/api/menu'

export default {
  name: 'ProductList',
  props: {
    menuCode: {
      type: String,
      required: true
    },
    parentMenuId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      topImage: '',
      activeSeriesCode: '',
      productList: [],
      loading: false,
      listIconSrc: require('@/assets/images/list_icon1.png')
    }
  },
  computed: {
    // 从 Vuex 获取所有菜单
    allMenus () {
      return this.$store.getters.allMenus
    },
    // 从 Vuex 获取产品中心菜单
    productCenterMenu () {
      return this.$store.getters.getProductCenterMenu
    },
    // 从 Vuex 获取产品系列列表
    seriesList () {
      return this.$store.getters.getProductSeries
    },
    // 判断当前菜单是否是产品中心本身
    isProductCenter () {
      if (!this.productCenterMenu) {
        return false
      }
      return this.menuCode.toUpperCase() === this.productCenterMenu.code.toUpperCase()
    },
    // 当前列表所有产品图片，用于点击预览（可左右切换）
    previewImageList () {
      return this.productList.map(p => this.getProductImage(p)).filter(Boolean)
    }
  },
  watch: {
    menuCode () {
      this.initData()
    },
    seriesList: {
      immediate: true,
      handler () {
        if (this.seriesList.length > 0 && !this.activeSeriesCode) {
          // 如果当前菜单是产品中心本身，不设置默认系列
          if (this.isProductCenter) {
            // 产品中心本身，不设置默认系列，查询全部产品
            this.loadAllProducts()
          } else {
            // 如果当前菜单是产品中心的子菜单，使用它作为默认系列
            const currentMenu = this.allMenus.find(menu => menu.code && menu.code.toUpperCase() === this.menuCode.toUpperCase())
            if (currentMenu && this.productCenterMenu && currentMenu.parentId === this.productCenterMenu.id) {
              this.activeSeriesCode = currentMenu.code
              this.loadProducts(currentMenu.code)
            } else {
              // 否则使用第一个系列
              this.activeSeriesCode = this.seriesList[0].code
              this.loadProducts(this.seriesList[0].code)
            }
          }
        }
      }
    }
  },
  mounted () {
    this.initData()
  },
  methods: {
    async initData () {
      // 获取产品中心的 banner 图片
      await this.loadTopImage()
      // 加载系列列表（产品中心的子菜单）由 computed 属性自动获取
      // 加载默认系列的产品（由 watch 处理）
    },
    async loadTopImage () {
      // 获取产品中心的 banner 图片
      try {
        if (this.productCenterMenu) {
          // 获取产品中心菜单的内容详情
          const contentRes = await getMenuHtmlDetail({ webMenuId: this.productCenterMenu.id })
          if (contentRes && contentRes.result && contentRes.result.htmlContent) {
            try {
              const parsedData = JSON.parse(contentRes.result.htmlContent)
              if (parsedData.bannerImage) {
                this.topImage = parsedData.bannerImage
              }
            } catch (e) {
              // 如果不是 JSON，忽略
            }
          }
        }
      } catch (error) {
        console.error('获取顶部图片失败:', error)
      }
    },
    async loadProducts (seriesCode) {
      if (!seriesCode) {
        return
      }
      this.loading = true
      try {
        const res = await queryDynamicHtmlList({
          webCode: seriesCode.toUpperCase(),
          type: '99',
          pageNo: 1,
          pageSize: 100, // 产品列表可能需要显示更多
          status: 2 // 只显示已生效的
        })
        if (res && res.result) {
          // 接口返回的数据已经过滤了 status=2，直接使用
          this.productList = res.result.records || []
        }
      } catch (error) {
        console.error('加载产品列表失败:', error)
        this.productList = []
      } finally {
        this.loading = false
      }
    },
    async loadAllProducts () {
      // 查询全部产品，不传 webCode
      this.loading = true
      try {
        const res = await queryDynamicHtmlList({
          type: '99',
          pageNo: 1,
          pageSize: 100, // 产品列表可能需要显示更多
          status: 2 // 只显示已生效的
        })
        if (res && res.result) {
          // 接口返回的数据已经过滤了 status=2，直接使用
          this.productList = res.result.records || []
        }
      } catch (error) {
        console.error('加载全部产品失败:', error)
        this.productList = []
      } finally {
        this.loading = false
      }
    },
    handleSeriesClick (series) {
      if (series && series.code) {
        this.activeSeriesCode = series.code
        this.loadProducts(series.code)
      } else {
        // 点击"全部"或空值，查询全部产品
        this.activeSeriesCode = ''
        this.loadAllProducts()
      }
    },
    getProductImage (product) {
      // 解析 htmlContent 中的 JSON
      if (product.htmlContent) {
        try {
          const parsed = JSON.parse(product.htmlContent)
          if (parsed.image) {
            return parsed.image
          }
        } catch (e) {
          // 解析失败，忽略
        }
      }
      // 使用 icon 字段
      if (product.icon) {
        return product.icon
      }
      return null
    },
    getProductName (product) {
      // 解析 htmlContent 中的 JSON
      if (product.htmlContent) {
        try {
          const parsed = JSON.parse(product.htmlContent)
          if (parsed.name) {
            return parsed.name
          }
        } catch (e) {
          // 解析失败，忽略
        }
      }
      // 使用 title 字段
      return product.title || '未命名产品'
    },
    handleProductClick (product) {
      // 可以跳转到产品详情页，或者显示产品详情
      // 暂时不做处理，后续可以扩展
      console.log('点击产品:', product)
    },
    handleImageError (event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.product-list {
  width: 100%;
  background: #fff;
}

.top-image-section {
  width: 100%;
  margin-bottom: 40px;
}

.top-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.series-tabs-container {
  margin: 0 auto;
  padding: 0 20px;
}

.series-tabs {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #e8e8e8;
  flex-wrap: wrap;
}

.series-tab {
  padding: 16px 24px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.3s;
  white-space: nowrap;
  position: relative;
  z-index: 2;
}
.series-tab::after {
  height: 8px;
  border-radius: 4px;
  background-color: #fdd000;
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 0%;
  transition: all 0.5s;
  z-index: 1;
  opacity: 0.5;
}
.series-tab:hover::after {
  width: 110%;
}

.series-name {
  display: block;
}

.series-tab-icon {
  position: absolute;
  left: 50%;
  top: 60%;
  margin-top: 6px;
  width: 28px;
  height: 28px;
  transform: translateX(-50%) translateY(10px);
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  pointer-events: none;
}

.series-tab:hover .series-tab-icon {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.series-tab-active {
  color: #52c41a;
  border-bottom-color: #52c41a;
  font-weight: 600;
}

.products-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #666;
  gap: 12px;
}

.empty-products {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px 24px;
}

.product-item {
  cursor: pointer;
  transition: all 0.3s;
}

.product-item:hover {
  transform: translateY(-4px);
}

.product-item:hover .product-image {
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.2);
}

.product-image {
  width: 100%;
  height: 240px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  margin-bottom: 16px;
  transition: all 0.3s;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* el-image 填满容器，预览兼容 H5 */
.product-image .el-image {
  width: 100%;
  height: 100%;
  display: block;
}
.product-image .el-image ::v-deep .el-image__inner {
  width: 100%;
  height: 100%;
}
.product-image .el-image ::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 48px;
}

.product-name {
  font-size: 16px;
  color: #333;
  text-align: center;
  line-height: 1.5;
  font-weight: 500;
}

/* 移动端 */
@media (max-width: 768px) {
  .series-tabs-container {
    padding: 0 16px;
  }

  .series-tab {
    padding: 12px 16px;
    font-size: 14px;
  }

  .products-container {
    padding: 24px 16px;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 16px;
  }

  .product-image {
    height: 180px;
  }

  .product-name {
    font-size: 14px;
  }
}

/* 小屏幕 */
@media (max-width: 480px) {
  .products-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .product-image {
    height: 200px;
  }
}
</style>
